// @tennis-matches/services/governance/self-service-clubs.service
//
// Self-Service Vereinsanlage aus dem Hub ("Verein verbinden" →
// "Mein Verein ist nicht dabei"):
//   1. RPC `public.create_self_service_club_and_bind`
//      → legt Tenant SOFORT aktiv an, bindet User als `player`,
//        prüft 3er-Lifetime-Limit (rejected zählt nicht).
//   2. Best-effort Mail-Notification an support@tennis-matches.de
//      über den Express-Endpoint (Audit + Spam-Schutz).
//
// Die UI ruft `createSelfServiceClubFlowRemote` und bekommt eine
// strukturierte Response inkl. typisierter Fehler-Codes (`limit-reached`,
// `missing-club-name`, `rpc-error`).

import { getSupabaseClient } from "../supabase/index.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

async function __authHeaders() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const { data } = await supabase.auth.getSession();
  const tok = __asStr(data?.session?.access_token);
  if (!tok) return { ok: false, reason: "no-auth" };
  return {
    ok: true,
    headers: {
      Authorization: `Bearer ${tok}`,
      "Content-Type": "application/json",
    },
  };
}

/**
 * Liest die Limit-Belegung (creation_count / max_count / remaining) des
 * aktuellen Nutzers. UI nutzt das, um vorab eine Warnung zu zeigen,
 * bevor der User das Formular öffnet.
 */
export async function loadSelfServiceClubLimitRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };
  const { data, error } = await supabase.rpc(
    "count_my_self_service_club_creations",
  );
  if (error) {
    return { ok: false, reason: "rpc-error", error };
  }
  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: payload.ok !== false,
    creationCount: Number(payload.creation_count) || 0,
    maxCount: Number(payload.max_count) || 3,
    remaining: Number(payload.remaining) || 0,
  };
}

/**
 * Legt einen neuen Verein über die Self-Service-RPC an.
 * Erkennt das 3er-Limit explizit als `reason: "limit-reached"`.
 */
export async function createSelfServiceClubRemote({
  clubName,
  city,
  website,
  contact,
  requestedRoles,
} = {}) {
  const name = __asStr(clubName);
  if (!name) return { ok: false, reason: "missing-club-name" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc(
    "create_self_service_club_and_bind",
    {
      p_club_name: name,
      p_city: __asStr(city) || null,
      p_website: __asStr(website) || null,
      p_contact: __asStr(contact) || null,
      p_requested_roles:
        Array.isArray(requestedRoles) && requestedRoles.length
          ? requestedRoles.map(__asStr).filter(Boolean)
          : ["player"],
    },
  );

  if (error) {
    const msg = __asStr(error?.message).toLowerCase();
    if (msg.includes("self-service-limit-reached")) {
      return { ok: false, reason: "limit-reached", error };
    }
    if (msg.includes("self-club-assignment-locked")) {
      return { ok: false, reason: "self-club-assignment-locked", error };
    }
    if (msg.includes("missing-club-name")) {
      return { ok: false, reason: "missing-club-name", error };
    }
    if (msg.includes("not-authenticated")) {
      return { ok: false, reason: "not-authenticated", error };
    }
    return { ok: false, reason: "rpc-error", error };
  }

  const payload = data && typeof data === "object" ? data : {};
  return {
    ok: payload.ok !== false,
    tenantId: __asStr(payload.tenant_id) || null,
    creationId: __asStr(payload.creation_id) || null,
    roleBindingId: __asStr(payload.role_binding_id) || null,
    creationCount: Number(payload.creation_count) || 0,
    maxCount: Number(payload.max_count) || 3,
    clubName: __asStr(payload.club_name) || name,
    city: __asStr(payload.city) || __asStr(city),
  };
}

/**
 * Triggert best-effort die Support-Mail (Audit/Review-Workflow).
 * UI bricht NICHT ab, wenn der Mail-Versand fehlschlägt — der Verein
 * existiert bereits in der DB. Support kann den Audit-Datensatz
 * jederzeit direkt prüfen.
 */
export async function notifySelfServiceClubReviewRemote({ creationId } = {}) {
  const cid = __asStr(creationId);
  if (!cid) return { ok: false, reason: "missing-creation-id" };

  const auth = await __authHeaders();
  if (!auth.ok) return auth;

  try {
    const r = await fetch("/api/governance/self-service-clubs/notify-review", {
      method: "POST",
      headers: auth.headers,
      body: JSON.stringify({ creationId: cid }),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j?.ok) {
      return {
        ok: false,
        reason: __asStr(j?.reason || j?.detail || "request-failed"),
        detail: j?.detail,
      };
    }
    return {
      ok: true,
      mode: __asStr(j.mode) || "mock",
      recipientCount: Number(j.recipientCount) || 0,
      sent: Number(j.sent) || 0,
    };
  } catch (e) {
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * Komfort-Wrapper für die UI: RPC + Mail in einem Schritt.
 * Mail-Fehler werden als `mailReason` durchgereicht, brechen den Flow
 * aber NICHT ab (der Verein existiert dann trotzdem).
 */
export async function createSelfServiceClubFlowRemote(input) {
  const create = await createSelfServiceClubRemote(input);
  if (!create.ok) return create;

  const notify = await notifySelfServiceClubReviewRemote({
    creationId: create.creationId,
  });
  return {
    ...create,
    mailOk: notify.ok === true,
    mailReason: notify.ok ? undefined : __asStr(notify.reason) || undefined,
  };
}

/**
 * Verbindet den Nutzer direkt mit einem bestehenden Verein (player/parent),
 * ohne Access-Request oder Admin-Freigabe.
 */
export async function joinExistingTenantSelfServiceRemote({
  tenantId,
  requestedRoles,
} = {}) {
  const tid = __asStr(tenantId);
  if (!tid) return { ok: false, reason: "missing-tenant-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const roles =
    Array.isArray(requestedRoles) && requestedRoles.length
      ? requestedRoles.map(__asStr).filter(Boolean)
      : ["player"];

  const { data, error } = await supabase.rpc(
    "join_existing_tenant_self_service",
    {
      p_tenant_id: tid,
      p_requested_roles: roles,
    },
  );

  if (error) {
    const msg = __asStr(error?.message).toLowerCase();
    if (msg.includes("not-authenticated")) {
      return { ok: false, reason: "not-authenticated", error };
    }
    return { ok: false, reason: "rpc-error", error };
  }

  const payload = data && typeof data === "object" ? data : {};
  if (payload.ok === false) {
    return {
      ok: false,
      reason: __asStr(payload.reason) || "rpc-failed",
      payload,
    };
  }

  return {
    ok: true,
    tenantId: __asStr(payload.tenant_id) || tid,
    grantedCount: Number(payload.granted_count) || 0,
    granted: Array.isArray(payload.granted) ? payload.granted : [],
  };
}

/**
 * Beendet die eigene Vereinsverbindung (alle role_bindings für den Tenant).
 * Der letzte verbleibende Verein kann nicht gelöst werden (`last-tenant-not-allowed`).
 */
export async function leaveTenantSelfServiceRemote({ tenantId } = {}) {
  const tid = __asStr(tenantId);
  if (!tid) return { ok: false, reason: "missing-tenant-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc("leave_tenant_self_service", {
    p_tenant_id: tid,
  });

  if (error) {
    const msg = __asStr(error?.message).toLowerCase();
    const code = __asStr(error?.code);
    if (
      code === "42883" ||
      code === "PGRST202" ||
      msg.includes("leave_tenant_self_service")
    ) {
      return { ok: false, reason: "rpc-unavailable", error };
    }
    if (msg.includes("not-authenticated")) {
      return { ok: false, reason: "not-authenticated", error };
    }
    return { ok: false, reason: "rpc-error", error };
  }

  const payload = data && typeof data === "object" ? data : {};
  if (payload.ok === false) {
    return {
      ok: false,
      reason: __asStr(payload.reason) || "rpc-failed",
      payload,
    };
  }

  return {
    ok: true,
    tenantId: __asStr(payload.tenant_id) || tid,
    removedBindings: Number(payload.removed_bindings) || 0,
  };
}
